import { toAISdkMessages } from '@mastra/ai-sdk/ui'
import { MastraClient } from '@mastra/client-js'
import { DefaultChatTransport, type UIMessage } from 'ai'
import { supabase } from '@/lib/supabase/client'

/**
 * Iris ⇄ Mastra wiring.
 *
 * Two transports, deliberately separate:
 *  - **Chat streaming** uses AI SDK's `DefaultChatTransport` against the Mastra
 *    `chatRoute` at `/chat/iris` (v7 wire format), consumed by `useChat`.
 *  - **Thread management** uses `@mastra/client-js` against Mastra's built-in
 *    memory routes under `/api`.
 *
 * Both authenticate with a fresh Supabase JWT per request (access tokens rotate
 * ~hourly) and send the workspace as `x-iris-workspace-id` for the server's tool
 * context. Memory threads + working memory are scoped to the user: we pass the
 * `userId` as the memory `resource`, and the server's auth provider
 * (`mapUserToResourceId`) authoritatively sets the same id, so user memory
 * follows them across workspaces.
 */

export const IRIS_BASE_URL = import.meta.env.VITE_IRIS_BASE_URL as
  string | undefined
export const IRIS_AGENT_ID = 'iris'

/** Whether Iris is configured for this build. Gate UI on this. */
export const isIrisConfigured = Boolean(IRIS_BASE_URL)

async function freshAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? ''
}

/**
 * A `MastraClient` for memory-thread management, authed as the current user and
 * scoped to the workspace. Built per call so the JWT is always fresh.
 */
export async function createIrisClient(
  workspaceId: string
): Promise<MastraClient> {
  if (!IRIS_BASE_URL) {
    throw new Error('Iris is not configured (VITE_IRIS_BASE_URL is not set).')
  }
  return new MastraClient({
    baseUrl: IRIS_BASE_URL,
    headers: {
      Authorization: `Bearer ${await freshAccessToken()}`,
      'x-iris-workspace-id': workspaceId,
    },
  })
}

/**
 * Load a thread's stored history as AI SDK v7 `UIMessage[]`, ready to seed
 * `useChat`. Mastra's `toAISdkMessages` converter rebuilds the proper AI SDK
 * shape from the stored messages — including tool parts (so generation cards
 * survive recall) and per-message `metadata.createdAt` (timestamps). Returns
 * `[]` for a thread that doesn't exist yet.
 */
export async function fetchThreadUiMessages(
  threadId: string,
  workspaceId: string
): Promise<UIMessage[]> {
  if (!IRIS_BASE_URL) {
    return []
  }
  const client = await createIrisClient(workspaceId)
  try {
    const { messages } = await client
      .getMemoryThread({ threadId, agentId: IRIS_AGENT_ID })
      .listMessages()
    return toAISdkMessages(messages, { version: 'v7' })
  } catch (error) {
    // A thread that doesn't exist yet (a brand-new conversation whose metadata
    // is still being created) has no history to seed.
    if (
      error instanceof Error &&
      'status' in error &&
      (error as { status?: number }).status === 404
    ) {
      return []
    }
    throw error
  }
}

/**
 * The AI SDK chat transport for `useChat`.
 *
 * `prepareSendMessagesRequest` always attaches a fresh JWT + the org/workspace
 * headers and trims to the newest message (the server loads history from
 * memory). Per-send dynamics — the durable attachment paths and, for a brand-new
 * conversation, the freshly minted thread id — are passed by the caller via
 * `sendMessage(msg, { headers, body })` and merged here. `threadId` is the
 * default memory thread for sends that DON'T pass their own (notably the
 * auto-resume after an MCP approval); pass the current `activeThreadId`.
 */
export function createIrisChatTransport(opts: {
  orgId: string
  workspaceId: string
  userId: string
  threadId: string | undefined
}) {
  if (!IRIS_BASE_URL) {
    throw new Error('Iris is not configured (VITE_IRIS_BASE_URL is not set).')
  }
  const { orgId, workspaceId, userId, threadId } = opts
  return new DefaultChatTransport({
    api: `${IRIS_BASE_URL}/chat/${IRIS_AGENT_ID}`,
    prepareSendMessagesRequest: async ({ messages, headers, body }) => ({
      headers: {
        Authorization: `Bearer ${await freshAccessToken()}`,
        'x-iris-org-id': orgId,
        'x-iris-workspace-id': workspaceId,
        // OAuth landing for connect_mcp_server; origin allowlisted server-side.
        'x-iris-app-origin': `${window.location.origin}/auth/mcp-callback`,
        // Lets scheduled times resolve to the user's local zone (e.g. "3pm").
        'x-iris-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(headers as Record<string, string> | undefined),
      },
      body: {
        memory: {
          thread: threadId,
          resource: userId,
        },
        ...(body as Record<string, unknown> | undefined),
        messages: messages.slice(-1),
      },
    }),
  })
}
